/* Assets & Helpers */
import { getDate } from '@/assets/scripts/dateBuilders'
/* i18n */
import { useI18n } from 'vue-i18n'

export const firstCharUppercase = (string) => {
	return string.substring(0, 1).toUpperCase() + string.substring(1)
}

export const firstCharLowercase = (string) => {
	return string.substring(0, 1).toLowerCase() + string.substring(1)
}

/** Receives a string, and returns it with only first letter toUpperCase
@string { string }
@return { string }
*/
export const firstCharOfStringUppercase = (string) => {
	return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
}

/** Receives a string a format it like a Title
@string { string }
@return { string }
*/
export const titleCaseBuilder = (string) => {
	if (!string) return ''
	return string.replace(/\w\S*/g, (txt) => { return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase() })
}

/** Receives a string, and returns first and last word of it
@string { string }
@return { string }
*/
export const firstLastWordBuilder = string => {
	if (!string) return ''
	const allWords = string.toLowerCase().trim().split(' ')
	const twoWords = allWords.length > 1 ? `${allWords[0]} ${allWords[allWords.length - 1]}` : allWords[0]
	return twoWords
}

/** Receives a string, and returns first letter of first word and last word
@string { string }
@return { string }
*/
export const acronymBuilder = string => {
	if (!string) return ''
	if (Number(string)) return string.slice(0, 2)

	const allWords = string.trim().split(' ')
	const acronym = allWords[0][0] + (allWords.length > 1 ? allWords[allWords.length - 1][0] : '')
	return acronym
}

/** Receives initDate and/or endDate, and returns a string to use in date filter tag
@initDate { String }
@endDate { String }
@return { string }
*/
export const dateTagStringCreator = (initDate, endDate) => {
	const { t } = useI18n()
	let string

	if (!!initDate && !!endDate) {
		if (initDate === endDate) {
			string = getDate(initDate)
		} else {
			string = t('filters.fromTo', { startDate: getDate(initDate), endDate: getDate(endDate) })
		}
	} else if (initDate) {
		string = t('filters.from', { startDate: getDate(initDate) })
	} else {
		string = t('filters.to', { endDate: getDate(endDate) })
	}

	return string
}

/** Receives a MDW regex validator with min-max and replaces min length to zero
@regexString { string } - regex to change
@return { string } - regex fixed
*/
export const removeMinLengthFromRegex = regexString => {
	/* Finding indexes for min&max length of regex */
	const initMinMaxIndex = regexString.indexOf('{')
	const endMinMaxIndex = regexString.indexOf('}') + 1

	const minMaxValues = regexString.slice(initMinMaxIndex, endMinMaxIndex)
	const minValue = minMaxValues.split(',')[0]
	/* Replacing min for 1, so on input this regex is valid */
	const parsedMinMaxValue = minValue.replace(minValue.slice(1), 0) + ',' + minMaxValues.split(',')[1]
	/* Returning our regex with min value reseted to 1 */
	return regexString.slice(0, initMinMaxIndex) + parsedMinMaxValue + regexString.slice(endMinMaxIndex)
}

/** Receives a string and removes all possible accents
@string { string } - string to remove accents
@return { string } - string without accents
*/
export const removeAccents = string => {
    const strAccents = string.split('')
    let strAccentsOut = new Array([])
    const strAccentsLen = strAccents.length
    const accents = 'ÀÁÂÃÄÅàáâãäåÒÓÔÕÕÖØòóôõöøÈÉÊËèéêëðÇçÐÌÍÎÏìíîïÙÚÛÜùúûüÑñŠšŸÿýŽž'
    const accentsOut = 'AAAAAAaaaaaaOOOOOOOooooooEEEEeeeeeCcDIIIIiiiiUUUUuuuuNnSsYyyZz'
    for (let y = 0; y < strAccentsLen; y++) {
        if (accents.indexOf(strAccents[y]) !== -1) {
            strAccentsOut[y] = accentsOut.substr(accents.indexOf(strAccents[y]), 1)
        } else { strAccentsOut[y] = strAccents[y] }
    }
    strAccentsOut = strAccentsOut.join('')
    return strAccentsOut
}

/** Receives a currency string to convert
@string { string } - string to convert
@return { string } - string converted
*/

export const convertCurrencyString = string => {
		return string === 'AKZ' ? 'Kz' : string
}

/** Receives account number
@string { string }
@return { string }
*/
export const accountNumberFiller = string => {
	return string.toString().padStart(14, '0')
}

/** Receives string
@string { string }
@return { string }
*/
export const genericStringFiller = (string, maxValue, charToFill) => {
	return string.toString().padStart(maxValue, charToFill)
}

/** Receives account number
@string { string }
@return { string }
*/
export const accountFormatHelper = account => {
	if (account.includes('DW')) {
		return account.slice(0, 10) + ' ' + account.slice(10, 12) + ' ' + account.slice(12, 15)
	} else if (account.length >= 14) {
		return account.slice(0, 9) + ' ' + account.slice(9, 11) + ' ' + account.slice(11, 14)
	} else {
		accountFormatHelper(accountNumberFiller(account))
	}
}

/** Receives and formats card number
@cardNumber { string }
@return { string }
*/
export const cardNumberMaskedFormat = cardNumber => {
	const noSpaces = cardNumber.replaceAll(' ', '')

	return `${noSpaces.slice(0, 4)} ${noSpaces.slice(4, 8)} ${noSpaces.slice(8, 12)} ${noSpaces.slice(12, 16)}`
}

/** Receives and formats iban
@iban { string }
@return { string }
*/
export const ibanFormat = iban => {
	return iban.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ')
}

/** Receives data and ecrypt it
@data { string }
@return { string }
*/
export const encryptData = async data => {
	const msgUint8 = new TextEncoder().encode(data) // encode as (utf-8) Uint8Array
	const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8) // hash the message
	const hashArray = Array.from(new Uint8Array(hashBuffer)) // convert buffer to byte array
	const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('') // convert bytes to hex string

	return hashHex
}
